import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextareaAutosize,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {Check, Clear, KeyboardArrowRight} from '@material-ui/icons';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {tableConfig} from '../../../config/table-config';

const {
  DATA_PACKAGE: {columns: dataPackageColumns},
} = tableConfig;

const useStyles = makeStyles(() => ({
  dataPackageFRomEntryListItem: {
    color: '#212121',
    padding: '5px 7px',
    fontWize: '13px',
    wordWrap: 'break-word',
    whiteSpace: 'wrap',
    textOverflow: 'wrap',
    backgroundColor: '#f4f4f4',
  },
  tableHead: {
    color: '#212121',
    padding: '5px 7px',
    fontWize: '13px',
    wordWrap: 'break-word',
    whiteSpace: 'wrap',
    textOverflow: 'wrap',
    backgroundColor: '#e6e6e6',
    position: 'sticky',
    top: 0,
    zIndex: 10000,
  },
  ruidEntryInput: {display: 'block', width: '100%', resize: 'vertical'},
  formControlContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  foundAssetsTable: {
    marginBottom: '15px',
  },
  loading: {display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginTop: 20},
  accordionSummaryStyles: {display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between'},

  foundAssetsTableWrapper: {maxHeight: '500px', overflowX: 'scroll', marginBottom: '15px'},
}));

const ManageAssetsFromEntry = ({
  onRUIDEntryFormSubmit,
  dataPackageCollectionSetToCompareAgainst,
  ruidsFromEntry,
  dataPackagesFromEntryMap,
  onComponentUnmount,
  selectedTableRows,
  setSelectedTableRows,
}) => {
  const classes = useStyles();

  const [validating, setValidating] = useState(false);

  const tableCellCount = dataPackageCollectionSetToCompareAgainst ? 6 : 5;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => onComponentUnmount(), []);

  useEffect(() => {
    setValidating(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPackagesFromEntryMap]);

  const handleSubmitRUIDEntryForm = async evt => {
    evt.preventDefault();
    evt.stopPropagation();
    const formValues = new FormData(evt.target);
    const ruidEntryString = formValues.get('ruid-entry');

    if (ruidEntryString === '') return;

    setValidating(true);
    onRUIDEntryFormSubmit(ruidEntryString);
  };

  const DataPackageFromEntryTableRow = ({ruid, rowIndex}) => {
    const dataPackage = dataPackagesFromEntryMap.get(ruid);
    const [isCellExpanded, setCellExpanded] = useState(false);

    const handleCheckboxClick = evt => {
      const {checked} = evt.target;

      const auxSelectedRuids = [...selectedTableRows];
      auxSelectedRuids[rowIndex] = checked;
      setSelectedTableRows(auxSelectedRuids);
    };

    return (
      <>
        <TableRow>
          <TableCell padding="none" size="medium">
            <IconButton disabled={!dataPackage} onClick={() => setCellExpanded(prev => !prev)}>
              <KeyboardArrowRight />
            </IconButton>
          </TableCell>

          <TableCell padding="none" size="medium">
            <Checkbox
              checked={selectedTableRows[rowIndex]}
              color="primary"
              disabled={!dataPackage}
              onChange={handleCheckboxClick}
            />
          </TableCell>
          {dataPackageCollectionSetToCompareAgainst && (
            <TableCell align="center">
              {dataPackage && dataPackageCollectionSetToCompareAgainst.has(dataPackage.dataPackageId) ? (
                <Check style={{color: 'green'}} />
              ) : (
                <Clear style={{color: 'red'}} />
              )}
            </TableCell>
          )}
          <TableCell className={classes.dataPackageFRomEntryListItem} padding="normal" size="medium">
            {ruid}
          </TableCell>
          <TableCell className={classes.dataPackageFRomEntryListItem} padding="normal" size="medium">
            {dataPackage ? dataPackage.asset.name : 'Not found'}
          </TableCell>
          <TableCell align="center" className={classes.dataPackageFRomEntryListItem} padding="normal" size="medium">
            {dataPackage ? <Check style={{color: 'green'}} /> : <Clear style={{color: 'red'}} />}
          </TableCell>
        </TableRow>
        {dataPackage && (
          <TableRow>
            <TableCell colSpan={tableCellCount} padding="none">
              <Collapse in={isCellExpanded}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {dataPackageColumns.map(configColumn => (
                        <TableCell className={classes.tableHead} key={configColumn.id}>
                          <strong>{configColumn.label === 'RUID' ? 'Echostar ID' : configColumn.label}</strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{dataPackage.asset.echostarId}</TableCell>
                      <TableCell>{dataPackage.est ? <Check /> : <Clear />}</TableCell>
                      <TableCell>{dataPackage.network?.name}</TableCell>
                      <TableCell>{dataPackage.resolution}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  DataPackageFromEntryTableRow.propTypes = {
    rowIndex: PropTypes.number,
    ruid: PropTypes.string,
  };

  return (
    <>
      <form style={{marginBottom: '25px'}} onSubmit={handleSubmitRUIDEntryForm}>
        <div className={classes.formControlContainer}>
          <label htmlFor="ruidentry">
            <strong>RUID Input</strong>
          </label>
          <Button type="submit" variant="contained">
            Validate Entry
          </Button>
        </div>
        <TextareaAutosize
          className={classes.ruidEntryInput}
          id="ruidentry"
          maxRows={10}
          minRows={3}
          name="ruid-entry"
          placeholder="RUIDs"
        />
      </form>
      {validating ? (
        <div className={classes.loading}>
          Validating
          <CircularProgress />
        </div>
      ) : ruidsFromEntry.length > 0 ? (
        <>
          <div className={classes.foundAssetsTableWrapper}>
            <Table>
              <TableHead>
                <TableRow className={classes.tableHead}>
                  <TableCell padding="none" size="small" />
                  <TableCell padding="none" size="small" />
                  {dataPackageCollectionSetToCompareAgainst && (
                    <TableCell>
                      <strong>Added</strong>
                    </TableCell>
                  )}
                  <TableCell align="left" size="small">
                    <strong>RUID</strong>
                  </TableCell>
                  <TableCell align="left" size="small">
                    <strong>Name</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Valid</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ruidsFromEntry.map((ruid, rowIndex) => (
                  <DataPackageFromEntryTableRow key={ruid} rowIndex={rowIndex} ruid={ruid} selected={rowIndex} />
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      ) : (
        <div>
          <Typography>No RUIDs selected yet</Typography>
        </div>
      )}
    </>
  );
};

ManageAssetsFromEntry.propTypes = {
  dataPackageCollectionSetToCompareAgainst: PropTypes.object,
  dataPackagesFromEntryMap: PropTypes.object,
  onComponentUnmount: PropTypes.func,
  onRUIDEntryFormSubmit: PropTypes.func,
  ruidsFromEntry: PropTypes.array,
  selectedTableRows: PropTypes.array,
  setSelectedTableRows: PropTypes.func,
};

ManageAssetsFromEntry.defaultProps = {
  onComponentUnmount: () => {},
  ruidsFromEntry: [],
  dataPackagesFromEntryMap: new Map(),
};

export default ManageAssetsFromEntry;
